import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Alert, Button, Drawer, Input, Spin, Tag, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, MessageOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { reloadAfterChange } from '../../redux/actions';
import { pushUndo } from '../../redux/utils/undoHistory';
import FormattedText from './FormattedText';
import './styles.scss';

/**
 * Copilot chat drawer (backend: src/routes/copilot/copilotRoutes.ts).
 *
 * Claude can read the model freely; every value or formula change is shown
 * as a card the user approves or rejects. Applied changes go on the normal
 * undo stack and the open scenario is reloaded, as after an undo.
 */
const Copilot = () => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    // { role: 'user' | 'assistant' | 'error' | 'decision' | 'applied', ... }
    const [messages, setMessages] = useState([]);
    const [pendingChanges, setPendingChanges] = useState(null);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const bottomRef = useRef(null);

    const addMessage = (message) => setMessages((prev) => [...prev, message]);

    // The server decides whether CFOPilot is on for this server and organization; hidden until it says so.
    useEffect(() => {
        let active = true;
        axios.get('/copilot/settings')
            .then(({ data }) => active && setEnabled(data?.enabled === true))
            .catch(() => active && setEnabled(false));
        return () => { active = false; };
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, pendingChanges, busy]);

    const applyLocally = useCallback(async (applied) => {
        // One approval is one undo step, however many cells it changed.
        // Created accounts, entities and scenarios have no undo snapshot.
        const undoable = applied.filter((change) => change.undo);
        if (undoable.length > 0) {
            const label = undoable.length === 1
                ? `CFOPilot: ${undoable[0].title}`
                : `CFOPilot: ${undoable.length} changes (${undoable[0].title}, …)`;
            dispatch(pushUndo(label, undoable.map((change) => change.undo)));
        }

        const used = (tool) => applied.some((change) => change.tool === tool);
        try {
            await dispatch(reloadAfterChange({
                formulasChanged: used('set_formula'),
                accountsAdded: used('create_account'),
                customTabsChanged: used('create_custom_tab') || used('add_to_custom_tab'),
                entitiesChanged: used('create_entity') || used('create_scenario'),
                businessCasesChanged: used('create_business_case') || used('update_business_case'),
                accountSettingsChanged: used('set_business_case_settings'),
                allocationsChanged: used('create_allocation') || used('copy_allocations'),
                reportsChanged: used('create_report') || used('update_report') || used('add_report_accounts'),
                scenarioIds: applied.map((change) => change.undo?.scenarioId).filter((id) => id != null),
            }));
        } catch (err) {
            console.error(err);
            addMessage({ role: 'error', text: 'The changes were saved, but the table could not be refreshed. Reload the page.' });
        }
    }, [dispatch]);

    const handleReply = useCallback(async (reply) => {
        setConversationId(reply.conversationId);

        if (reply.appliedChanges?.length) {
            addMessage({
                role: 'applied',
                count: reply.appliedChanges.length,
                undoable: reply.appliedChanges.some((change) => change.undo),
            });
            await applyLocally(reply.appliedChanges);
        }
        if (reply.text) addMessage({ role: 'assistant', text: reply.text });
        if (reply.status === 'approval_required') setPendingChanges(reply.pendingChanges);
    }, [applyLocally]);

    const send = async () => {
        const text = draft.trim();
        if (!text || busy || pendingChanges) return;

        addMessage({ role: 'user', text });
        setDraft('');
        setBusy(true);
        try {
            const { data } = await axios.post('/copilot/messages', { conversationId, message: text });
            await handleReply(data);
        } catch (err) {
            addMessage({ role: 'error', text: errorText(err) });
        } finally {
            setBusy(false);
        }
    };

    const decide = async (approved) => {
        const changes = pendingChanges;
        setPendingChanges(null);
        addMessage({ role: 'decision', changes, approved });
        setBusy(true);
        try {
            const { data } = await axios.post(`/copilot/conversations/${conversationId}/approval`, { approved });
            await handleReply(data);
        } catch (err) {
            addMessage({ role: 'error', text: errorText(err) });
            // Some changes may have been saved before the error; show what's in the database.
            if (approved) dispatch(reloadAfterChange({ formulasChanged: true })).catch(console.error);
        } finally {
            setBusy(false);
        }
    };

    const startNewConversation = () => {
        setConversationId(null);
        setMessages([]);
        setPendingChanges(null);
        setDraft('');
    };

    if (!enabled) return null;

    return (
        <>
            {!open && (
                <Tooltip title="Ask CFOPilot" placement="left">
                    <Button
                        className="copilot-launcher"
                        type="primary"
                        shape="circle"
                        size="large"
                        icon={<MessageOutlined />}
                        onClick={() => setOpen(true)}
                    />
                </Tooltip>
            )}

            <Drawer
                className="copilot-drawer"
                title="CFOPilot"
                placement="right"
                width={440}
                mask={false}
                visible={open}
                onClose={() => setOpen(false)}
                bodyStyle={{ display: 'flex', flexDirection: 'column', padding: 0 }}
                extra={
                    <Button size="small" icon={<PlusOutlined />} onClick={startNewConversation} disabled={busy}>
                        New conversation
                    </Button>
                }
            >
                <div className="copilot-messages">
                    {messages.length === 0 && (
                        <div className="copilot-empty">
                            Ask CFOPilot about your model, for example <em>"What is revenue in 2026 in Base DK?"</em>, or ask
                            for a change. Every change is shown to you for approval first.
                        </div>
                    )}

                    {messages.map((message, i) => <MessageView key={i} message={message} />)}

                    {pendingChanges && (
                        <ChangeCard
                            changes={pendingChanges}
                            footer={
                                <div className="copilot-change-actions">
                                    <Button icon={<CloseOutlined />} onClick={() => decide(false)}>Reject</Button>
                                    <Button type="primary" icon={<CheckOutlined />} onClick={() => decide(true)}>
                                        {pendingChanges.length === 1 ? 'Apply change' : `Apply ${pendingChanges.length} changes`}
                                    </Button>
                                </div>
                            }
                        />
                    )}

                    {busy && <div className="copilot-busy"><Spin size="small" /> Working…</div>}
                    <div ref={bottomRef} />
                </div>

                <div className="copilot-input">
                    <Input.TextArea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onPressEnter={(e) => {
                            if (e.shiftKey) return;
                            e.preventDefault();
                            send();
                        }}
                        placeholder={pendingChanges ? 'Apply or reject the changes above first' : 'Ask CFOPilot…'}
                        autoSize={{ minRows: 1, maxRows: 6 }}
                        disabled={busy || !!pendingChanges}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={send}
                        disabled={busy || !!pendingChanges || !draft.trim()}
                    />
                </div>
            </Drawer>
        </>
    );
};

const MessageView = ({ message }) => {
    switch (message.role) {
        case 'user':
            return <div className="copilot-bubble copilot-bubble-user">{message.text}</div>;
        case 'assistant':
            return <div className="copilot-bubble copilot-bubble-assistant"><FormattedText text={message.text} /></div>;
        case 'error':
            return <Alert className="copilot-alert" type="error" showIcon message={message.text} />;
        case 'applied':
            return (
                <Alert
                    className="copilot-alert"
                    type="success"
                    showIcon
                    message={`${message.count === 1 ? '1 change' : `${message.count} changes`} applied.${message.undoable ? ' Use Undo to revert.' : ''}`}
                />
            );
        case 'decision':
            return (
                <ChangeCard
                    changes={message.changes}
                    decided
                    footer={<Tag color={message.approved ? 'green' : 'default'}>{message.approved ? 'Approved' : 'Rejected'}</Tag>}
                />
            );
        default:
            return null;
    }
};

const ChangeCard = ({ changes, footer, decided = false }) => (
    <div className={`copilot-change-card${decided ? ' copilot-change-card-decided' : ''}`}>
        {!decided && <div className="copilot-change-heading">Proposed {changes.length === 1 ? 'change' : 'changes'}</div>}
        {changes.map((change) => (
            <div key={change.id} className="copilot-change">
                <div className="copilot-change-title">
                    {change.title}
                    <Tag color={change.scope === 'All scenarios' ? 'orange' : 'blue'}>{change.scope}</Tag>
                </div>
                <div className="copilot-change-diff">
                    <span className="copilot-change-before">{change.before}</span>
                    <span className="copilot-change-arrow">→</span>
                    <span className="copilot-change-after">{change.after}</span>
                </div>
                {change.warning && !decided && <div className="copilot-change-warning">{change.warning}</div>}
            </div>
        ))}
        {footer}
    </div>
);

function errorText(err) {
    return err.response?.data?.error ?? 'CFOPilot could not be reached. Please try again.';
}

export default Copilot;
